import React from 'react';
import AuthenticatedInput from '../AuthenticatedInput';
import AuthenticatedSelect from '../AuthenticatedSelect';
import { updateExamField } from '../../utils/examData';
import { combine } from '../../utils/amaMath';
import {
  calculateFlexion, calculateExtension, calculateAbduction, calculateAdduction,
  calculateInternalRotation, calculateExternalRotation,
} from '../TabRatingNarratives';
import { useExamTab } from './ExamTabContext';

const ShouldersTab: React.FC = () => {
  const {
    examData, setExamData, user, userProfile, startManualSession, setShowAuthModal,
    copyRegionValues, handleClearRegion, clearConfirmRegion, fillNormalValues,
  } = useExamTab();

  return (
                <div>
                  <h3 className="text-2xl font-semibold mb-6">Shoulders</h3>

                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">

                  {/* Shoulder Rating Section — live WPI instrument (right column) */}
                  {examData?.shoulders && (
                    <div className="lg:order-2 lg:sticky lg:top-20">
                      <div className="card p-4 flex flex-col gap-4">
                        <div>
                          <span className="font-medium">Right Shoulder WPI: </span>
                          <span className="ml-2 text-xl font-bold">
                          {(() => {
                            const data = examData?.shoulders;
                            if (!data) return "--";
                            
                            // Check if right shoulder has ROM values
                            if (data.right_flexion === null && 
                                data.right_extension === null && 
                                data.right_abduction === null && 
                                data.right_adduction === null && 
                                data.right_internal_rotation === null && 
                                data.right_external_rotation === null) {
                              return "--";
                            }
                            
                            // Calculate individual impairments
                            const rightFlexion = calculateFlexion(data.right_flexion);
                            const rightExtension = calculateExtension(data.right_extension);
                            const rightAbduction = calculateAbduction(data.right_abduction);
                            const rightAdduction = calculateAdduction(data.right_adduction);
                            const rightInternalRotation = calculateInternalRotation(data.right_internal_rotation);
                            const rightExternalRotation = calculateExternalRotation(data.right_external_rotation);
                            
                            // Sum individual impairments
                            let rightShoulderRating = rightFlexion + rightExtension + rightAbduction + rightAdduction + 
                                                  rightInternalRotation + rightExternalRotation;
                            
                            // Add acromioplasty if included
                            if (data.right_includeAcromioplasty) {
                              rightShoulderRating = combine(rightShoulderRating, 10);
                            }
                            
                            // Convert to Whole Person Impairment (60% of upper extremity rating)
                            const rightWPI = Math.round(rightShoulderRating * 0.6);
                            
                            return `${rightWPI}%`;
                          })()}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Left Shoulder WPI: </span>
                          <span className="ml-2 text-xl font-bold">
                          {(() => {
                            const data = examData?.shoulders;
                            if (!data) return "--";
                            
                            // Check if left shoulder has ROM values
                            if (data.left_flexion === null && 
                                data.left_extension === null && 
                                data.left_abduction === null && 
                                data.left_adduction === null && 
                                data.left_internal_rotation === null && 
                                data.left_external_rotation === null) {
                              return "--";
                            }
                            
                            // Calculate individual impairments
                            const leftFlexion = calculateFlexion(data.left_flexion);
                            const leftExtension = calculateExtension(data.left_extension);
                            const leftAbduction = calculateAbduction(data.left_abduction);
                            const leftAdduction = calculateAdduction(data.left_adduction);
                            const leftInternalRotation = calculateInternalRotation(data.left_internal_rotation);
                            const leftExternalRotation = calculateExternalRotation(data.left_external_rotation);
                            
                            // Sum individual impairments
                            let leftShoulderRating = leftFlexion + leftExtension + leftAbduction + leftAdduction + 
                                                 leftInternalRotation + leftExternalRotation;
                            
                            // Add acromioplasty if included
                            if (data.left_includeAcromioplasty) {
                              leftShoulderRating = combine(leftShoulderRating, 10);
                            }
                            
                            // Convert to Whole Person Impairment (60% of upper extremity rating)
                            const leftWPI = Math.round(leftShoulderRating * 0.6);
                            
                            return `${leftWPI}%`;
                          })()}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Left column: acromioplasty checkboxes + range of motion */}
                  <div className="lg:order-1">
                  {/* Acromioplasty Checkboxes */}
                  <div className="flex items-center mb-6">
                    <input
                      type="checkbox"
                      id="rightShoulderAcromioplasty"
                      className={`mr-2 ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
                      checked={examData?.shoulders?.right_includeAcromioplasty || false}
                      onChange={(e) => {
                        if (!user) return;
                        const checked = e.target.checked;
                        setExamData(prev => prev ? {
                          ...prev,
                          shoulders: {
                            ...prev.shoulders,
                            right_includeAcromioplasty: checked
                          }
                        } : null);
                      }}
                      disabled={!user}
                    />
                    <label htmlFor="rightShoulderAcromioplasty" className="text-sm">Include Right Shoulder Acromioplasty</label>
                    
                    <input
                      type="checkbox"
                      id="leftShoulderAcromioplasty"
                      className={`ml-6 mr-2 ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
                      checked={examData?.shoulders?.left_includeAcromioplasty || false}
                      onChange={(e) => {
                        if (!user) return;
                        setExamData(prev => prev ? {
                          ...prev,
                          shoulders: {
                            ...prev.shoulders,
                            left_includeAcromioplasty: e.target.checked
                          }
                        } : null);
                      }}
                      disabled={!user}
                    />
                    <label htmlFor="leftShoulderAcromioplasty" className="text-sm">Include Left Shoulder Acromioplasty</label>
                  </div>
                  
                  {/* Range of Motion Section */}
                  <div className="mb-8">
                    <div className="flex justify-between items-center mb-4 border-b border-border pb-2">
                      <h4 className="text-lg font-medium">Range of Motion</h4>
                      <div className="flex gap-2 flex-wrap"><button onClick={() => copyRegionValues('shoulders', 'right_to_left')} className="btn-secondary text-xs" title="Copy Right values to Left">R → L</button><button onClick={() => copyRegionValues('shoulders', 'left_to_right')} className="btn-secondary text-xs" title="Copy Left values to Right">L → R</button><button onClick={() => handleClearRegion('shoulders')} className={clearConfirmRegion === 'shoulders' ? 'btn-danger text-xs' : 'btn-secondary text-xs'}>{clearConfirmRegion === 'shoulders' ? 'Confirm Clear?' : 'Clear Values'}</button><button onClick={() => fillNormalValues('shoulders')} className="btn-secondary text-xs" disabled={!user}>Fill Normal Values</button></div>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                      {/* Header Row */}
                      <div className="col-span-1"></div>
                      <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                      <div className="col-span-1 text-center font-medium">Right</div>
                      <div className="col-span-1 text-center font-medium">Left</div>

                      {/* Flexion Row */}
                      <div className="col-span-1 text-content-secondary">Flexion</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">180°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.shoulders?.right_flexion ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'shoulders', 'right_flexion', value));
                          }}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.shoulders?.left_flexion ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'shoulders', 'left_flexion', value));
                          }}
                        />
                      </div>

                      {/* Extension Row */}
                      <div className="col-span-1 text-content-secondary">Extension</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">50°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.shoulders.right_extension ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'shoulders', 'right_extension', value));
                          }}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.shoulders.left_extension ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'shoulders', 'left_extension', value));
                          }}
                        />
                      </div>

                      {/* Abduction Row */}
                      <div className="col-span-1 text-content-secondary">Abduction</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">180°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.shoulders.right_abduction ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'shoulders', 'right_abduction', value));
                          }}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.shoulders.left_abduction ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'shoulders', 'left_abduction', value));
                          }}
                        />
                      </div>

                      {/* Adduction Row */}
                      <div className="col-span-1 text-content-secondary">Adduction</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">50°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.shoulders.right_adduction ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'shoulders', 'right_adduction', value));
                          }}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.shoulders.left_adduction ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'shoulders', 'left_adduction', value));
                          }}
                        />
                      </div>

                      {/* Internal Rotation Row */}
                      <div className="col-span-1 text-content-secondary">Internal rotation</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">90°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.shoulders.right_internal_rotation ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'shoulders', 'right_internal_rotation', value));
                          }}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.shoulders.left_internal_rotation ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'shoulders', 'left_internal_rotation', value));
                          }}
                        />
                      </div>

                      {/* External Rotation Row */}
                      <div className="col-span-1 text-content-secondary">External rotation</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">90°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.shoulders.right_external_rotation ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'shoulders', 'right_external_rotation', value));
                          }}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.shoulders.left_external_rotation ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'shoulders', 'left_external_rotation', value));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  </div>
                  </div>

                </div>
  );
};

export default ShouldersTab;
